import React from 'react'
import { Switch, useLocation, Route, Router } from 'react-router-dom'
import { ThemeProvider, withStyles, StylesProvider } from '@material-ui/styles'
import { createGenerateClassName } from '@material-ui/core/styles'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import JssProvider from 'react-jss/lib/JssProvider'
import { Helmet } from 'react-helmet'
import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'

import 'moment/locale/pt-br'
import 'react-perfect-scrollbar/dist/css/styles.css'
import 'configs/yup'
import 'driver.js/dist/driver.css'
import 'leaflet/dist/leaflet.css'

import theme from 'theme'
import history from 'service/history'
import Routes from './Routes'
import SupplierRoutes from './SupplierRoutes'
import GuestRoutes from './GuestRoutes'
import TrainingRoutes from './TrainingRoutes'
import { GoogleAnalyticsGlobalTag } from 'components'

import AuthProvider from 'providers/AuthProvider'
import DaiChatProvider from 'providers/DaiChatProvider'
import DaiChatPanelSizeProvider from 'providers/DaiChatPanelSizeProvider'
import DaiOptOutProvider from 'providers/DaiOptOutProvider'
import SnackbarProvider from 'providers/SnackbarProvider'
import SelectCountryProvider from 'providers/SelectCountryProvider'
import CustomThemeProvider from 'providers/CustomThemeProvider'
import ContentHeaderProvider from 'providers/ContentHeaderProvider'
import AuthSupplierProvider from 'providers/AuthSupplierProvider'
import AuthGuestProvider from 'providers/AuthGuestProvider'
import AuthTrainingProvider from 'providers/AuthTrainingProvider'
import RegulationsProvider from 'providers/RegulationsProvider'
import ExternalServicesProvider from 'providers/ExternalServicesProvider'

import useAuthSupplier from 'hooks/useAuthSupplier'
import useAuth from 'hooks/useAuth'
import useAuthGuest from 'hooks/useAuthGuest'

import * as service from 'service'
import { getApplicationUrl, isProduction } from 'service/env'
import constants from 'constants/index'
import authConfig from 'helpers/microsoftLogin/authLogin'

const defaultGenerateClassName = createGenerateClassName({
  disableGlobal: true,
  productionPrefix: 'c',
})

const msalInstance = new PublicClientApplication(authConfig)

const App = () => (
  <>
    <Helmet>
      <link rel="canonical" href={getApplicationUrl()} />
      {isProduction && <script>{constants.googleTagManager.SCRIPT}</script>}
    </Helmet>
    <JssProvider generateClassName={defaultGenerateClassName}>
      <ThemeProvider theme={theme}>
        <MuiPickersUtilsProvider locale="pt-br" utils={MomentUtils}>
          <StylesProvider injectFirst>
            <SnackbarProvider>
              <Router history={history}>
                <ScrollToTop />
                <Switch>
                  <Route path="/training">
                    <AuthTrainingProvider>
                      <TrainingRoutes />
                    </AuthTrainingProvider>
                  </Route>
                  <Route path="/:organizationSlug/guests">
                    <AuthGuestProvider>
                      <CustomThemeProvider useAuthHook={useAuthGuest}>
                        <DaiOptOutProvider
                          useAuthHook={useAuthGuest}
                          fetchService={service.guestPortal.daiOptOut.get}
                        >
                          <ContentHeaderProvider>
                            <RegulationsProvider>
                              <GuestRoutes />
                            </RegulationsProvider>
                          </ContentHeaderProvider>
                        </DaiOptOutProvider>
                      </CustomThemeProvider>
                    </AuthGuestProvider>
                  </Route>
                  <Route path={['/suppliers', '/:organizationSlug/suppliers']}>
                    <AuthSupplierProvider>
                      <CustomThemeProvider useAuthHook={useAuthSupplier}>
                        <ContentHeaderProvider>
                          <SupplierRoutes />
                        </ContentHeaderProvider>
                      </CustomThemeProvider>
                    </AuthSupplierProvider>
                  </Route>
                  <Route>
                    <AuthProvider>
                      <ExternalServicesProvider>
                        <CustomThemeProvider useAuthHook={useAuth}>
                          <DaiChatPanelSizeProvider>
                            <DaiOptOutProvider>
                              <DaiChatProvider>
                                <SelectCountryProvider>
                                  <ContentHeaderProvider>
                                    <RegulationsProvider>
                                      <MsalProvider instance={msalInstance}>
                                        <Routes />
                                      </MsalProvider>
                                    </RegulationsProvider>
                                  </ContentHeaderProvider>
                                </SelectCountryProvider>
                              </DaiChatProvider>
                            </DaiOptOutProvider>
                          </DaiChatPanelSizeProvider>
                        </CustomThemeProvider>
                      </ExternalServicesProvider>
                    </AuthProvider>
                  </Route>
                </Switch>
              </Router>
            </SnackbarProvider>
          </StylesProvider>
        </MuiPickersUtilsProvider>
      </ThemeProvider>
    </JssProvider>
    <GoogleAnalyticsGlobalTag />
  </>
)

const styles = {
  '@global': {
    '*': {
      boxSizing: 'border-box',
      margin: 0,
      padding: 0,
    },
    html: {
      height: '100%',
      '-webkit-font-smoothing': 'antialiased',
      '-moz-osx-font-smoothing': 'grayscale',
    },
    body: {
      backgroundColor: '#f4f6f8',
      height: '100%',
    },
    a: {
      textDecoration: 'none',
    },
    '#root': {
      height: '100%',
    },
  },
}

function ScrollToTop() {
  const { pathname } = useLocation()

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

export default withStyles(styles)(App)
